import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { getProductBySlug, type PublicProductDetail } from '@/application/products/product-query-service';

const statusLabels: Record<string, string> = {
  available: 'Available Now',
  reserved: 'Reserved',
  sold: 'Sold',
  preorder_only: 'Preorder Only',
};

export function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<PublicProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(0);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getProductBySlug(slug)
      .then(setProduct)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-[var(--color-brand-muted)]">Loading...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold">Product Not Found</h1>
        <Link to="/inventory" className="mt-4 inline-block text-[var(--color-brand-primary)] hover:underline">
          &larr; Back to collection
        </Link>
      </div>
    );
  }

  const currentPhoto = product.photos[selectedPhoto];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Link to="/inventory" className="text-sm text-[var(--color-brand-muted)] hover:text-[var(--color-brand-primary)]">
        &larr; Back to collection
      </Link>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Photo gallery */}
        <div>
          <div className="aspect-square bg-[var(--color-glam-cream)] rounded-xl overflow-hidden">
            {currentPhoto ? (
              <img
                src={currentPhoto.url}
                alt={currentPhoto.altText ?? product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-6xl">
                ✨
              </div>
            )}
          </div>
          {product.photos.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {product.photos.map((photo, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedPhoto(i)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 ${
                    i === selectedPhoto
                      ? 'border-[var(--color-brand-primary)]'
                      : 'border-transparent'
                  }`}
                >
                  <img
                    src={photo.url}
                    alt={photo.altText ?? ''}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product info */}
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">{product.name}</h1>
            <span className="text-sm px-3 py-1 rounded-full bg-[var(--color-glam-cream)] font-medium">
              {statusLabels[product.status] ?? product.status}
            </span>
          </div>

          {product.price && (
            <p className="mt-3 text-2xl font-bold text-[var(--color-brand-primary)]">
              ${product.price}
            </p>
          )}

          <p className="mt-4 text-[var(--color-brand-muted)]">{product.description}</p>

          {product.detailNotes && (
            <div className="mt-4 p-4 bg-[var(--color-glam-cream)] rounded-lg">
              <h3 className="text-sm font-semibold mb-1">Details</h3>
              <p className="text-sm text-[var(--color-brand-muted)]">
                {product.detailNotes}
              </p>
            </div>
          )}

          <div className="mt-4">
            <span className="text-sm font-medium">
              Size{product.sizes.length > 1 ? 's' : ''}: {product.sizes.join(', ')}
            </span>
          </div>

          <p className="mt-4 text-xs text-[var(--color-brand-muted)] italic">
            Each pair is handmade and unique. Custom work may vary from displayed items.
          </p>

          {/* Preorder CTA */}
          <div className="mt-8">
            <Link
              to={`/preorder?product=${product.slug}`}
              className="block w-full text-center bg-[var(--color-brand-accent)] text-white py-3 rounded-full font-medium hover:opacity-90 transition-opacity"
            >
              {product.status === 'available' ? 'Reserve This Pair' : 'Request Similar Pair'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
